import { useState, useEffect } from "react";
import { TextField, InputAdornment, IconButton, Alert } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { LoginContainer, LoginBox, LogoBox, StyledIcon, StyledButton, BackgroundBox } from "../styles/LoginStyles";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp > currentTime) {
          navigate("/home");
        } else {
          localStorage.clear();
        }
      } catch {
        localStorage.clear();
      }
    }
  }, [navigate]);

  useEffect(() => {
    const handleAutoFill = () => {
      const emailInput = document.querySelector('input[type="email"]');
      const passwordInput = document.querySelector('input[type="password"]');

      if (emailInput?.value && emailInput.value !== formData.email) {
        setFormData((prev) => ({ ...prev, email: emailInput.value }));
      }
      if (passwordInput?.value && passwordInput.value !== formData.password) {
        setFormData((prev) => ({ ...prev, password: passwordInput.value }));
      }
    };

    const timer = setTimeout(handleAutoFill, 300);

    const inputs = document.querySelectorAll("input");
    inputs.forEach((input) => {
      input.addEventListener("animationstart", handleAutoFill);
    });

    return () => {
      clearTimeout(timer);
      inputs.forEach((input) => {
        input.removeEventListener("animationstart", handleAutoFill);
      });
    };
  }, [formData.email, formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "email" ? value.trim() : value,
    }));
    if (error) setError(null);
  };

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError("Todos los campos son obligatorios");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Por favor ingresa un email válido");
      return;
    }

    const API = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;

    if (!API) {
      setError("No está configurada la URL del backend (REACT_APP_BACKEND_URL).");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API}/api/auth/login`,
        formData,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000,
        }
      );

      const { data } = response;

      if (data.success && data.token) {
        localStorage.setItem("token", data.token);

        const decoded = jwtDecode(data.token);

        localStorage.setItem("userRole", decoded.role?.toString() || "");
        localStorage.setItem("userSede", decoded.sede || "");
        localStorage.setItem("userId", decoded.id?.toString() || "");
        localStorage.setItem("userEmail", decoded.email || "");
        localStorage.setItem("idSeller", decoded.id_sellers?.toString() || "");

        try {
          const permissionsResponse = await axios.get(
            `${API}/api/permisos/me`,
            { headers: { Authorization: `Bearer ${data.token}` }, timeout: 10000 }
          );
          localStorage.setItem(
            "userPermissions",
            JSON.stringify(permissionsResponse.data?.data?.permisos || {})
          );
        } catch (permissionError) {
          console.warn("No se pudieron cargar los permisos; se usará el acceso por rol.", permissionError);
          localStorage.removeItem("userPermissions");
        }

        const userName =
          decoded.name ||
          decoded.username ||
          decoded.nombre ||
          data.user?.name ||
          data.user?.username ||
          data.user?.nombre ||
          data.name ||
          "CRIS";

        localStorage.setItem("userName", userName);

        if (data.user) {
          localStorage.setItem("userData", JSON.stringify(data.user));
        }

        navigate("/home", { replace: true });
      } else {
        throw new Error(data.message || "Error en la respuesta del servidor");
      }
    } catch (err) {
      console.error("❌ Error en login:", {
        error: err.message,
        response: err.response?.data,
      });

      let errorMessage = "Error al iniciar sesión";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.code === "ECONNABORTED") {
        errorMessage = "Tiempo de espera agotado. Intenta nuevamente.";
      } else if (err.request) {
        errorMessage = "No se pudo conectar con el servidor";
      }

      setError(errorMessage);

      localStorage.removeItem("token");
      localStorage.removeItem("userData");
      localStorage.removeItem("userName");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <BackgroundBox />
      <LoginContainer>
        <form onSubmit={handleSubmit} noValidate autoComplete="on">
          <LoginBox>
            <LogoBox />
            <StyledIcon />

            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              margin="normal"
              value={formData.email}
              onChange={handleChange}
              error={!!error && !formData.email}
              required
              autoFocus
              autoComplete="email"
              placeholder="usuario@ejemplo.com"
            />

            <TextField
              label="Contraseña"
              name="password"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={formData.password}
              onChange={handleChange}
              required
              error={!!error && !formData.password}
              autoComplete="current-password"
              placeholder="••••••••"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword}
                      edge="end"
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                {error}
              </Alert>
            )}

            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{ mt: 3 }}
            >
              {isSubmitting ? "INICIANDO SESIÓN..." : "INICIAR SESIÓN"}
            </StyledButton>
          </LoginBox>
        </form>
      </LoginContainer>
    </>
  );
};

export default Login;